import { useState } from 'react';
import { AlertTriangle, BookOpen, ChevronDown, ExternalLink, Eye, ListOrdered } from 'lucide-react';
import { usePlaybooks } from '../hooks/usePlaybooks';
import { Card, CardBody } from '../components/ui/Card';
import { Spinner } from '../components/ui/Spinner';
import { EmptyState } from '../components/ui/EmptyState';
import { getApiErrorMessage } from '../api/client';
import { formatDate } from '../utils/format';
import { cn } from '../utils/cn';
import type { Playbook } from '../types';

function JiraBadge({ jiraKey, jiraBaseUrl }: { jiraKey: string; jiraBaseUrl: string | null }) {
  const className =
    'inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500 dark:bg-slate-800 dark:text-slate-400';
  if (!jiraBaseUrl) {
    return <span className={className}>{jiraKey}</span>;
  }
  return (
    <a
      href={`${jiraBaseUrl}/browse/${jiraKey}`}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => e.stopPropagation()}
      className={cn(className, 'hover:bg-slate-200 dark:hover:bg-slate-700')}
    >
      {jiraKey} <ExternalLink size={9} />
    </a>
  );
}

function PlaybookCard({
  playbook,
  jiraBaseUrl,
  defaultOpen = false,
}: {
  playbook: Playbook;
  jiraBaseUrl: string | null;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Card>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-3 p-5 text-left"
      >
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-pulse-600 dark:text-pulse-400">
              {playbook.category}
            </p>
            {playbook.relatedJiraKeys?.map((key) => <JiraBadge key={key} jiraKey={key} jiraBaseUrl={jiraBaseUrl} />)}
          </div>
          <p className="mt-0.5 font-semibold text-slate-900 dark:text-white">{playbook.title}</p>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{playbook.summary}</p>
        </div>
        <ChevronDown size={18} className={cn('shrink-0 text-slate-400 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <CardBody className="border-t border-slate-100 pt-4 dark:border-slate-800">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div>
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
                <Eye size={12} /> What to look for
              </p>
              <ul className="space-y-1.5">
                {playbook.whatToLookFor.map((item, i) => (
                  <li key={i} className="flex gap-2 text-sm text-slate-600 dark:text-slate-300">
                    <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-slate-400" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
                <ListOrdered size={12} /> Steps
              </p>
              <ol className="space-y-3">
                {playbook.steps.map((step, i) => (
                  <li key={i} className="flex gap-2.5 text-sm">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-pulse-100 text-[11px] font-semibold text-pulse-700 dark:bg-pulse-900/40 dark:text-pulse-300">
                      {i + 1}
                    </span>
                    <div>
                      <p className="font-medium text-slate-800 dark:text-slate-100">{step.title}</p>
                      <p className="text-slate-500 dark:text-slate-400">{step.detail}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          <p className="mt-4 text-[11px] text-slate-400">Last reviewed {formatDate(playbook.lastReviewed)}</p>
        </CardBody>
      )}
    </Card>
  );
}

export function Playbooks() {
  const { data, isLoading, isError, error } = usePlaybooks();

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Spinner size={28} />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <Card className="p-8 text-center">
        <AlertTriangle className="mx-auto mb-3 text-rose-500" size={28} />
        <p className="font-medium text-slate-700 dark:text-slate-200">Couldn&apos;t load playbooks</p>
        <p className="mt-1 text-sm text-slate-400">{getApiErrorMessage(error)}</p>
      </Card>
    );
  }

  if (data.playbooks.length === 0) {
    return (
      <EmptyState
        icon={BookOpen}
        title="No playbooks yet"
        description="Add playbooks in backend/src/config/playbooks.ts - they'll show up here automatically."
      />
    );
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-slate-400">
        Starting-point procedures for audits and staff awareness - review and adjust to how Health Academy actually
        operates, then keep the review date current. Playbooks tagged with a Jira key are the drafted answer to that
        backlog ticket - review it, then close the ticket instead of starting from a blank page.
      </p>
      <div className="space-y-3">
        {data.playbooks.map((playbook, i) => (
          <PlaybookCard key={playbook.id} playbook={playbook} jiraBaseUrl={data.jiraBaseUrl} defaultOpen={i === 0} />
        ))}
      </div>
    </div>
  );
}
